import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Box, Typography, Paper, Chip } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import deviceEventStore from "../../../../stores/DeviceEventStore";
import MonthlyPicker from "./MonthlyPicker";
import PaginationControl from "../../../table-component/PaginationControl";

const renderBoldHeader = (params) => (
  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
    {params?.colDef?.headerName || ""}
  </Typography>
);

const DeviceConnection = observer(() => {
  const { pimios_id } = useParams();
  const actualDeviceId = pimios_id?.split("-")[0];
  const [selectedMonth, setSelectedMonth] = useState(() =>
    new Date().toISOString().slice(0, 7)
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const currentMonth = deviceEventStore.getMonthKey(`${selectedMonth}-01`);

  const connectionEvents = useMemo(() => {
    if (!actualDeviceId) return [];
    return deviceEventStore.getConnectionEvents(currentMonth, actualDeviceId);
  }, [currentMonth, actualDeviceId, deviceEventStore.connectionEventsMap]);

  useEffect(() => {
    if (actualDeviceId && connectionEvents.length === 0) {
      console.log(`No connection events for ${actualDeviceId}`);
    }
  }, [actualDeviceId]);

  // useEffect(() => {
  //   console.log("📦 Full connectionEventsMap:");
  //   for (const [month, deviceMap] of deviceEventStore.connectionEventsMap.entries()) {
  //     console.log(`📅 Month: ${month}`);
  //     for (const [deviceId, events] of deviceMap.entries()) {
  //       console.log(`  🔧 Device ID: ${deviceId} — ${events.length} events`);
  //       console.table(events.map(e => ({
  //         timestamp: e.timestamp?.$date || e.timestamp,
  //         status: e.payload?.status,
  //       })));
  //     }
  //   }
  // }, [deviceEventStore.connectionEventsMap]);

  const columns = useMemo(
    () => [
      {
        field: "timestamp",
        headerName: "Timestamp",
        flex: 1,
        minWidth: 220,
        renderHeader: renderBoldHeader,
        renderCell: (params) => {
          const value = params.value;
          const date = value ? new Date(value) : null;
          return date
            ? date.toLocaleString(undefined, {
                hour12: true,
                hour: "numeric",
                minute: "numeric",
                second: "numeric",
                fractionalSecondDigits: 3,
                year: "numeric",
                month: "numeric",
                day: "numeric",
              })
            : "Invalid Date";
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        minWidth: 160,
        renderHeader: renderBoldHeader,
        renderCell: (params) => {
          const isConnected = params.value === "connected";
          const config = {
            label: isConnected ? "Connected" : "Disconnected",
            icon: isConnected ? (
              <CheckCircleOutlinedIcon
                sx={{ color: "#fff", fontSize: "1rem" }}
              />
            ) : (
              <CancelOutlinedIcon sx={{ color: "#fff", fontSize: "1rem" }} />
            ),
            bgColor: isConnected ? "#16a34a" : "#dc2626",
            fontWeight: 700,
            textColor: "#fff",
          };

          return (
            <Chip
              icon={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pl: "2px",
                  }}
                >
                  {config.icon}
                </Box>
              }
              label={
                <Typography
                  sx={{
                    fontWeight: config.fontWeight,
                    fontSize: "0.8rem",
                    textTransform: "none",
                  }}
                >
                  {config.label}
                </Typography>
              }
              sx={{
                minWidth: 150,
                px: 1.5,
                py: 0.5,
                height: "auto",
                borderRadius: "999px",
                backgroundColor: config.bgColor,
                color: config.textColor,
                textTransform: "none",
              }}
            />
          );
        },
      },
      {
        field: "trigger",
        headerName: "Triggered Reason",
        flex: 1,
        minWidth: 220,
        renderHeader: renderBoldHeader,
        renderCell: (params) => {
          const trigger = params.value || "N/A";
          return (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {trigger}
            </Typography>
          );
        },
      },
    ],
    []
  );

  const rows = useMemo(
    () =>
      connectionEvents.map((event, index) => ({
        id: event._id?.$oid || event._id || index,
        timestamp: event.timestamp?.$date || event.timestamp,
        status: event.payload?.status,
        trigger: event.payload?.trigger,
      })),
    [connectionEvents]
  );

  const totalPages = Math.max(Math.ceil(rows.length / pageSize), 1);
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = rows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <MonthlyPicker
          value={selectedMonth}
          onChange={setSelectedMonth}
          label="Month"
        />
      </Box>
      <Box>
        {deviceEventStore.loading ? (
          <Typography align="center" color="text.secondary">
            Loading...
          </Typography>
        ) : rows.length === 0 ? (
          <Typography align="center" color="text.secondary">
            No connection history recorded.
          </Typography>
        ) : (
          <>
            <Paper elevation={3} sx={{ height: 540, width: "100%" }}>
              <DataGridPro
                rows={paginatedRows}
                columns={columns}
                disableColumnMenu
                disableSelectionOnClick
                pagination={false}
                hideFooter={true}
                sx={{
                  borderRadius: 2,
                  boxShadow: 2,
                  border: "1px solid #e0e0e0",
                  backgroundColor: "#fafafa",
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f4f6f8",
                    fontWeight: "bold",
                    fontSize: "0.9rem",
                    borderBottom: "1px solid #ddd",
                  },
                  "& .MuiDataGrid-cell": {
                    fontSize: "0.875rem",
                    color: "#333",
                  },
                  "& .MuiDataGrid-row:nth-of-type(odd)": {
                    backgroundColor: "#ffffff",
                  },
                  "& .MuiDataGrid-row:nth-of-type(even)": {
                    backgroundColor: "#f7fbff",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#e3f2fd",
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                }}
              />
            </Paper>

            <PaginationControl
              page={currentPage}
              setPage={setPage}
              totalPages={totalPages}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </>
        )}
      </Box>
    </>
  );
});

export default DeviceConnection;
